import {
  ChartBarIcon,
  CodeBracketIcon,
  EyeIcon,
  RocketLaunchIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
    BoltIcon,
  CpuChipIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import {TeamPageSEO} from '../../seo/seo';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, easeOut: [0.4, 0, 0.2, 1] } },
};



const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

/* ---------------- HERO ---------------- */

function TeamsHero() {
  return (
    <section className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>
      <div className="mx-auto max-w-7xl px-6 py-32 text-start">

        {/* Caption */}

        
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="relative mx-auto max-w-7xl px-6 py-32 text-start"
        >
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-purple-500">
          Army of Experts, Armoured with AI
        </span>

          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-6xl dark:text-gray-100">
            A Team Built
            <span className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              for Complex Problems
            </span>
            Others Can’t Solve
          </h1>

          <p className="mt-8 max-w-3xl text-base leading-relaxed text-gray-600 text-start  sm:text-lg dark:text-gray-300">
            We don’t just offer architects, engineers, or data experts. 
            We build disciplined teams that think in systems, deliver with consistency, and work as a true extension of your company.
          </p>

          <p className="mt-4 max-w-2xl text-sm text-gray-500 text-start dark:text-gray-400">
            High-quality talent. Strong engineering culture. Cost-efficient execution.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- WHY HIRE ---------------- */

function HireBenefits() {
  const benefits = [
    {
      title: 'Engineers Who Deliver',
      desc: 'We select engineers based on execution, not resumes. Every team member is tested on real-world problem solving and delivery under pressure.',
      icon: CodeBracketIcon,
      gradient: 'from-indigo-400 via-purple-400 to-pink-400',
    },
    {
      title: 'Flexible Team Models',
      desc: 'Start with a small team and scale fast. Dedicated squads, part-time specialists, or full product teams.',
      icon: RocketLaunchIcon,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    },
    {
      title: 'Structured Execution',
      desc: 'Clear sprint cycles, strong ownership, and measurable outputs. No chaos, no confusion, just consistent progress.',
      icon: ChartBarIcon,
      gradient: 'from-yellow-400 via-orange-400 to-red-400',
    },
    {
      title: 'Full Transparency',
      desc: 'Daily visibility into work, direct communication with engineers, and clear reporting across every stage.',
      icon: EyeIcon,
      gradient: 'from-pink-400 via-rose-400 to-purple-400',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl px-6 py-28">
      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mb-12 text-center text-4xl font-extrabold text-gray-900 dark:text-gray-100"
      >
        Why Teams Choose Akwid Labs
      </motion.h2>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4"
      >
        {benefits.map((b, i) => {
          const Icon = b.icon;

          return (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ y: -8 }}
              className="relative rounded-3xl border p-8 shadow-sm backdrop-blur transition hover:shadow-xl dark:border-white/10 dark:bg-white/5"
            >
              <div
                className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${b.gradient} text-white`}
              >
                <Icon className="h-6 w-6" />
              </div>

              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {b.title}
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-300">{b.desc}</p>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

/* ---------------- APPROACH ---------------- */

function TeamApproach() {
  const steps = [
    {
      title: 'Understand the Problem',
      desc: 'We go deep into your product, users, and business goals before suggesting any team structure.',
      color: 'from-indigo-400 to-purple-500',
    },
    {
      title: 'Build the Right Team',
      desc: 'We assemble engineers who match your stack, pace, and expectations.',
      color: 'from-purple-400 to-pink-500',
    },
    {
      title: 'Embed & Execute',
      desc: 'Teams integrate into your workflow and start delivering from week one.',
      color: 'from-pink-400 to-rose-500',
    },
    {
      title: 'Scale & Improve',
      desc: 'We continuously refine performance and scale the team as your product grows.',
      color: 'from-amber-400 to-orange-500',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-6xl px-6 py-32">
      {/* subtle background glow */}
      <div className="absolute inset-0 -z-10 opacity-20 blur-3xl">
        <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400" />
      </div>

      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mb-20 text-center text-4xl font-extrabold text-gray-900 dark:text-gray-100"
      >
        How We Work With You
      </motion.h2>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="relative grid gap-16 md:grid-cols-2"
      >
        {steps.map((s, i) => (
          <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }} className="group relative">
            {/* connector line (desktop only) */}
            {i < steps.length - 1 && (
              <div className="absolute right-[-40px] top-12 hidden h-[2px] w-[80px] bg-gradient-to-r from-gray-300 to-transparent md:block dark:from-gray-700" />
            )}

            <div className="relative rounded-3xl border p-8 shadow-sm backdrop-blur transition-all duration-300 hover:shadow-2xl dark:border-white/10 dark:bg-white/5">
              {/* gradient accent bar */}
              <div
                className={`absolute left-0 top-0 h-full w-1 rounded-l-3xl bg-gradient-to-b ${s.color}`}
              />

              {/* step number with glow */}
              <div
                className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r ${s.color} text-sm font-bold text-white shadow-lg`}
              >
                {i + 1}
              </div>

              <h3 className="mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
                {s.title}
              </h3>

              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{s.desc}</p>

              {/* hover glow effect */}
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10 opacity-0 transition duration-300 group-hover:opacity-100" />
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function TeamOutcomes() {
  const outcomes = [
    {
      title: 'Faster Releases',
      desc: 'Move from idea to production faster with engineers who already know how to execute.',
      color: 'from-indigo-400 to-purple-500',
    },
    {
      title: 'Predictable Delivery',
      desc: 'Clear ownership, structured sprints, and fewer surprises during development.',
      color: 'from-purple-400 to-pink-500',
    },
    {
      title: 'Effortless Scaling',
      desc: 'Expand your team without breaking workflows or slowing progress.',
      color: 'from-pink-400 to-rose-500',
    },
    {
      title: 'Global Engineering Standard',
      desc: 'Work that meets the expectations of top international teams.',
      color: 'from-amber-400 to-orange-500',
    },
  ];

  return (
    <section className="relative mx-auto w-full max-w-5xl px-6 py-32">
      {/* background glow */}
      <div className="absolute inset-0 -z-10 opacity-20 blur-3xl">
        <div className="absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400" />
      </div>

      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mb-20 text-center text-4xl font-extrabold text-gray-900 dark:text-gray-100"
      >
        What You Actually Get
      </motion.h2>

      <div className="relative">
        {/* vertical line */}
        <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-gray-300 via-gray-200 to-transparent dark:from-gray-700 dark:via-gray-600" />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="space-y-16"
        >
          {outcomes.map((o, i) => {
            const isLeft = i % 2 === 0;

            return (
              <motion.div
                key={i}
                variants={fadeUp}
                className="relative flex items-center justify-between"
              >
                {/* LEFT CARD */}
                <div className={`w-[46%] ${isLeft ? 'block' : 'invisible md:visible'}`}>
                  {isLeft && <Card o={o} index={i} />}
                </div>

                {/* CENTER DOT */}
                <div className="relative z-10 flex items-center justify-center">
                  <div
                    className={`h-10 w-10 rounded-full bg-gradient-to-r ${o.color} flex items-center justify-center font-bold text-white shadow-lg`}
                  >
                    {i + 1}
                  </div>
                </div>

                {/* RIGHT CARD */}
                <div className={`w-[46%] ${!isLeft ? 'block' : 'invisible md:visible'}`}>
                  {!isLeft && <Card o={o} index={i} />}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* Reusable Card (keeps sizing consistent) */
function Card({ o, index }: any) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      className="group relative flex min-h-[150px] flex-col justify-center rounded-2xl border p-6 shadow-sm backdrop-blur transition-all duration-300 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
    >
      {/* gradient accent */}
      <div
        className={`absolute left-0 top-0 h-1 w-full rounded-t-2xl bg-gradient-to-r ${o.color}`}
      />

      <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">{o.title}</h3>

      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">{o.desc}</p>

      {/* hover glow */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10 opacity-0 transition duration-300 group-hover:opacity-100" />
    </motion.div>
  );
}

/* ---------------- SRI LANKA ADVANTAGE ---------------- */
function SriLankaAdvantage() {
  const items = [
    {
      title: 'Senior Talent, Smarter Cost',
      desc: 'Access engineers who operate at a high level without the overhead of Western hiring markets.',
      icon: CurrencyDollarIcon,
      color: 'from-emerald-400 to-teal-500',
    },
    {
      title: 'Strong Engineering Foundations',
      desc: 'A culture built on solid fundamentals, problem solving, and disciplined execution.',
      icon: ChartBarIcon,
      color: 'from-indigo-400 to-purple-500',
    },
    {
      title: 'Built for Global Teams',
      desc: 'Fluent communication, timezone flexibility, and experience working with international companies.',
      icon: GlobeAltIcon,
      color: 'from-pink-400 to-rose-500',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">
      {/* background glow */}
      <div className="absolute inset-0 -z-10 opacity-20 blur-3xl">
        <div className="absolute right-1/3 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-gradient-to-r from-emerald-400 via-indigo-400 to-pink-400" />
      </div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mb-20 text-center"
      >
        <h2 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100">
          Why Build Teams in Sri Lanka
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-gray-600 dark:text-gray-300">
          A practical choice for companies that want strong engineering without unnecessary
          overhead.
        </p>
      </motion.div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="grid gap-10 md:grid-cols-3"
      >
        {items.map((item, i) => {
          const Icon = item.icon;

          return (
            <motion.div key={i} variants={fadeUp} whileHover={{ y: -6 }} className="group relative">
              <div className="relative rounded-3xl border border-gray-200/60 bg-white/70 p-8 text-center shadow-sm backdrop-blur transition-all duration-300 hover:shadow-2xl dark:border-white/10 dark:bg-white/5">
                {/* gradient top accent */}
                <div
                  className={`absolute left-0 top-0 h-1 w-full rounded-t-3xl bg-gradient-to-r ${item.color}`}
                />

                {/* icon with glow */}
                <div
                  className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${item.color} text-white shadow-lg`}
                >
                  <Icon className="h-6 w-6" />
                </div>

                <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {item.title}
                </h3>

                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {item.desc}
                </p>

                {/* hover glow */}
                <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10 opacity-0 transition duration-300 group-hover:opacity-100" />
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

function BeforeAfterOutcome() {
  type Item = {
    title: string;
    before: string;
    after: string;
    impact?: string;
  };

  const data: Item[] = [
    {
      title: 'Product Delivery Speed',
      before: 'Release cycles took 6–8 weeks with frequent delays.',
      after: 'Predictable 2–3 week release cycles with stable delivery.',
      impact: '2.5x faster shipping',
    },
    {
      title: 'Engineering Capacity',
      before: 'Small in-house team struggling with scaling demands.',
      after: 'On-demand scalable engineering squad integrated in 2 weeks.',
      impact: '3x team capacity',
    },
    {
      title: 'System Stability',
      before: 'Frequent production issues and reactive fixes.',
      after: 'Structured QA + monitoring reduced critical incidents significantly.',
      impact: '60% fewer incidents',
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-6 py-28">

      <h2 className="mb-16 text-center text-4xl font-extrabold text-gray-900 dark:text-gray-100">
        Real Engineering Outcomes
      </h2>

      <div className="grid gap-10 md:grid-cols-3">

        {data.map((item, i) => (
          <div
            key={i}
            className="group relative rounded-3xl border border-gray-200/60 bg-white/60 p-8 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
          >

            {/* Impact badge */}
            {item.impact && (
              <div className="mb-6 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                {item.impact}
              </div>
            )}

            {/* Title */}
            <h3 className="mb-6 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {item.title}
            </h3>

            {/* BEFORE block */}
            <div className="mb-5 rounded-xl border border-red-100 bg-red-50/40 p-4 dark:border-red-500/20 dark:bg-red-500/5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-red-500">
                Before
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {item.before}
              </p>
            </div>

            {/* AFTER block */}
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-500">
                After
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {item.after}
              </p>
            </div>

            {/* subtle progress bar (visual contrast) */}
        

            {/* hover glow */}
            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10 opacity-0 transition duration-300 group-hover:opacity-100" />
          </div>
        ))}

      </div>
    </section>
  );
}

function AIAccelerationSection() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 py-32">

      {/* soft AI glow background */}
      <div className="absolute inset-0 -z-10 opacity-30 blur-3xl">
        <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400" />
      </div>

      {/* Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="text-center"
      >
        <h2 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100">
          Human Engineers,
          <span className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            AI-Accelerated Execution
          </span>
        </h2>

        <p className="mx-auto mt-4 max-w-2xl text-gray-600 dark:text-gray-300">
          We combine disciplined engineering teams with AI tools like Copilot, automation systems,
          and intelligent workflows to deliver faster, cleaner, and more reliable software.
        </p>
      </motion.div>

      {/* Feature Grid */}
      <div className="mt-20 grid gap-8 md:grid-cols-2">

        {/* Card 1 */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative rounded-3xl border p-8 backdrop-blur dark:border-white/10 dark:bg-white/5"
        >
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-6 w-6 text-purple-400" />
            <h3 className="text-lg font-semibold">AI-Assisted Development</h3>
          </div>

          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
            Engineers use tools like GitHub Copilot and internal AI agents to accelerate coding,
            reduce repetitive work, and improve accuracy.
          </p>

          {/* micro indicator */}
          <div className="mt-6 h-2 w-full rounded-full bg-gray-200 dark:bg-white/10">
            <div className="h-2 w-[78%] rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400" />
          </div>

          <p className="mt-2 text-xs text-gray-500">Up to 70% faster feature implementation</p>
        </motion.div>

        {/* Card 2 */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative rounded-3xl border p-8 backdrop-blur dark:border-white/10 dark:bg-white/5"
        >
          <div className="flex items-center gap-3">
            <BoltIcon className="h-6 w-6 text-indigo-400" />
            <h3 className="text-lg font-semibold">Human-First Engineering</h3>
          </div>

          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
            AI supports execution, but engineers own architecture, decisions, and quality.
            No automation without accountability.
          </p>

          {/* simple flow indicator */}
          <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
            <span className="rounded-full bg-indigo-400/20 px-2 py-1">Think</span>
            <span>→</span>
            <span className="rounded-full bg-purple-400/20 px-2 py-1">Build</span>
            <span>→</span>
            <span className="rounded-full bg-pink-400/20 px-2 py-1">Validate</span>
          </div>
        </motion.div>

        {/* Card 3 */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative rounded-3xl border p-8 backdrop-blur dark:border-white/10 dark:bg-white/5"
        >
          <div className="flex items-center gap-3">
            <CodeBracketIcon className="h-6 w-6 text-pink-400" />
            <h3 className="text-lg font-semibold">Faster Delivery Cycles</h3>
          </div>

          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
            AI-assisted QA, testing, and documentation reduce bottlenecks in every sprint.
          </p>

          <div className="mt-6 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-lg bg-indigo-400/10 py-2">Plan</div>
            <div className="rounded-lg bg-purple-400/10 py-2">Build</div>
            <div className="rounded-lg bg-pink-400/10 py-2">Ship</div>
          </div>
        </motion.div>

        {/* Card 4 */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative rounded-3xl border p-8 backdrop-blur dark:border-white/10 dark:bg-white/5"
        >
          <div className="flex items-center gap-3">
            <CpuChipIcon className="h-6 w-6 text-emerald-400" />
            <h3 className="text-lg font-semibold">AI-Augmented Systems</h3>
          </div>

          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
            Internal tooling and automation pipelines remove repetitive engineering tasks
            and improve consistency across teams.
          </p>

          {/* mini stat */}
          <div className="mt-6 text-2xl font-bold text-gray-900 dark:text-gray-100">
            2.3x
            <span className="ml-2 text-sm font-normal text-gray-500">
              productivity boost
            </span>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
/* ---------------- PAGE ---------------- */

export default function Team() {
  return (
    <div>
        <TeamPageSEO />
      <TeamsHero />
            <AIAccelerationSection />
      <TeamApproach />
      <BeforeAfterOutcome />
      <HireBenefits />
      <TeamOutcomes />
      <SriLankaAdvantage />

    </div>
  );
}
